/**
 * Live call audio capture - the two Twilio streams turned into one dual-channel
 * WAV file every N seconds. React-free on purpose: the encoder and the window
 * arithmetic are the parts most likely to be wrong, and they are testable here
 * without a browser.
 *
 * Channel order is not a detail: LEFT is the operator's own microphone, RIGHT is
 * the mix returned to this leg. On a `<Dial>` bridge that mix is exactly the
 * person who answered. On a CONFERENCE it is everyone else in the room, which
 * with two participants is still the customer - but a third participant would
 * break the description, and nothing here would notice. The after-call pass
 * makes the same distinction, in `lib/direction.ts`, so the live and the
 * after-call passes share one convention instead of contradicting each other.
 *
 * Why WAV rather than MediaRecorder:
 *  - MediaRecorder emits the container header only in the FIRST chunk, so every
 *    window after the first is an undecodable fragment on its own. The published
 *    workaround is stop()/start() per window, which then leaves the format up to
 *    whatever the browser feels like producing (Chrome: webm/opus).
 *  - Gemini's inline audio formats are wav/mp3/aiff/aac/ogg/flac. WebM is not on
 *    that list, so the browser's default output would have to be transcoded
 *    server-side, which means ffmpeg in the API image.
 * Tapping the graph and writing the RIFF header ourselves sidesteps both: every
 * window is a complete, standalone, Gemini-shaped file by construction.
 *
 * The tap is a READER. No failure in here - no microphone, no worklet, no
 * encoder - may disturb the call itself; the graph never touches the path the
 * audio takes to Twilio, and every error is reported and swallowed.
 */

/** 16 kHz is what speech recognition wants, and it is a quarter of the bytes of
 *  48 kHz. The AudioContext resamples both inputs to it for us. */
export const PILOT_SAMPLE_RATE = 16_000;

/** How many frames the tap batches before posting them to the main thread.
 *  128-frame quanta would be ~125 messages a second for no benefit. */
const TAP_BATCH_FRAMES = 4096;

/** A tail shorter than this is silence or half a word, not a window worth sending. */
const MIN_TAIL_MS = 2_000;

export type CaptureErrorCode =
  | "unsupported"
  | "no_local_stream"
  | "no_remote_stream"
  | "context_failed"
  | "tap_failed";

export interface CapturedWindow {
  /** 0-based window index within the call. */
  seq: number;
  /** Dual-channel 16-bit PCM WAV, complete with its own header. */
  blob: Blob;
  /** Wall clock at the first sample of this window. */
  startedAt: number;
  durationMs: number;
}

export interface LiveCaptureOptions {
  /** The operator's microphone, from `call.getLocalStream()`. */
  local: MediaStream;
  /** The far end, from `call.getRemoteStream()`. Null still captures the
   *  operator, with silence on the right - degraded, not broken. */
  remote: MediaStream | null;
  windowMs: number;
  /**
   * Where to start numbering. Not always zero: closing the panel stops the tap
   * and reopening it starts a new one, and a second run that began at zero again
   * would file its turns in front of the ones already stored.
   */
  startSeq?: number;
  onWindow: (window: CapturedWindow) => void;
  /** Reported, never thrown: the call outranks the co-pilot. */
  onError: (code: CaptureErrorCode) => void;
}

export interface LiveCaptureHandle {
  /** Tears the graph down and flushes a tail window if one is worth sending. */
  stop: () => void;
  /**
   * Stop cutting windows without tearing the capture down - the call is on hold.
   *
   * A held customer is removed from the mix in both directions, so the remote
   * stream is silence and the hold music is documented as never reaching the
   * recording either. Without this, a four-minute hold would spend sixteen
   * windows of the per-call budget on nothing.
   *
   * Pausing rather than stopping and restarting is deliberate: `startSeq` is
   * read once from a snapshot at start, so a restart would renumber windows on
   * top of ones the server has already stored.
   */
  setPaused: (paused: boolean) => void;
}

// ── The encoder (pure) ──────────────────────────────────────────────────────

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
}

/**
 * Interleaved 16-bit PCM WAV from one Float32Array per channel.
 *
 * Channels of unequal length are read to the LONGEST one and the short channel
 * is padded with silence. A dropped quantum on one side must not shift the other
 * side's speech in time, which is exactly what truncating to the shortest would
 * do - and a transcript where the two speakers drift apart is worse than one
 * with a gap in it.
 */
export function encodeWavPcm16(channels: Float32Array[], sampleRate: number): ArrayBuffer {
  const channelCount = Math.max(1, channels.length);
  const frames = channels.reduce((max, c) => Math.max(max, c.length), 0);
  const dataBytes = frames * channelCount * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // format 1 = uncompressed PCM
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channelCount * 2, true); // byte rate
  view.setUint16(32, channelCount * 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let frame = 0; frame < frames; frame += 1) {
    for (let ch = 0; ch < channelCount; ch += 1) {
      const source = channels[ch];
      const sample = source && frame < source.length ? source[frame]! : 0;
      // Clamp before scaling: a value above 1 wraps into loud noise otherwise.
      const clamped = sample < -1 ? -1 : sample > 1 ? 1 : sample;
      view.setInt16(offset, Math.round(clamped * 32767), true);
      offset += 2;
    }
  }
  return buffer;
}

// ── The window buffer (pure) ────────────────────────────────────────────────

/**
 * Frames as they arrive, cut into windows on an exact sample count.
 *
 * The cut is counted in samples rather than measured on a timer, so a window is
 * always the length it claims to be even when the page was busy and the batches
 * arrived late. `startedAt` is then derived from how many frames came before it,
 * which keeps the transcript's timeline honest across a long call.
 */
export class WindowBuffer {
  private readonly pending: Float32Array[][] = [[], []];
  private pendingFrames = 0;
  private emittedFrames = 0;

  constructor(
    private readonly windowFrames: number,
    private readonly sampleRate: number,
  ) {}

  push(left: Float32Array, right: Float32Array): void {
    this.pending[0]!.push(left);
    this.pending[1]!.push(right);
    this.pendingFrames += Math.max(left.length, right.length);
  }

  /** Whole windows are ready when at least one full window has accumulated. */
  get ready(): boolean {
    return this.pendingFrames >= this.windowFrames;
  }

  get bufferedMs(): number {
    return (this.pendingFrames / this.sampleRate) * 1000;
  }

  /** Milliseconds of audio already handed out - the offset of the next window. */
  get emittedMs(): number {
    return (this.emittedFrames / this.sampleRate) * 1000;
  }

  /**
   * Cut one window off the front. `all` takes whatever is left instead, which is
   * how the tail is flushed when the call ends mid-window.
   */
  take(all = false): { channels: Float32Array[]; frames: number; offsetMs: number } | null {
    const frames = all ? this.pendingFrames : this.windowFrames;
    if (frames <= 0 || (!all && this.pendingFrames < frames)) return null;

    const offsetMs = this.emittedMs;
    const channels = this.pending.map((chunks) => {
      const out = new Float32Array(frames);
      let written = 0;
      while (written < frames && chunks.length > 0) {
        const head = chunks[0]!;
        const room = frames - written;
        if (head.length <= room) {
          out.set(head, written);
          written += head.length;
          chunks.shift();
        } else {
          out.set(head.subarray(0, room), written);
          chunks[0] = head.subarray(room);
          written += room;
        }
      }
      return out;
    });

    this.pendingFrames = Math.max(0, this.pendingFrames - frames);
    this.emittedFrames += frames;
    return { channels, frames, offsetMs };
  }

  /**
   * Throw away everything buffered, counting it as time that passed.
   *
   * Used when the call is put on hold. Dropping the audio alone would glue the
   * seconds before the hold to the seconds after it into one window that reads
   * as a continuous sentence; advancing `emittedFrames` by the same amount
   * keeps every later window's `startedAt` honest about when it was actually
   * said, so a four-minute hold shows up as a four-minute gap rather than
   * silently shifting the rest of the call backwards.
   */
  discard(): void {
    this.pending[0]!.length = 0;
    this.pending[1]!.length = 0;
    this.emittedFrames += this.pendingFrames;
    this.pendingFrames = 0;
  }
}

/** Window length the tap will actually honour, whatever the setting says. */
export function clampWindowMs(ms: number): number {
  if (!Number.isFinite(ms)) return 15_000;
  return Math.min(60_000, Math.max(5_000, Math.round(ms)));
}

// ── The graph (browser) ─────────────────────────────────────────────────────

/**
 * The tap, as an AudioWorklet module. It is a string because the alternative is
 * a separate entry point in the Vite build for eleven lines of glue; a Blob URL
 * keeps it next to the code that uses it.
 */
const TAP_WORKLET = `
class HuslyPilotTap extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.size = (options && options.processorOptions && options.processorOptions.batchFrames) || 4096;
    this.left = new Float32Array(this.size);
    this.right = new Float32Array(this.size);
    this.at = 0;
  }
  flush() {
    if (this.at === 0) return;
    const left = this.left.slice(0, this.at);
    const right = this.right.slice(0, this.at);
    this.port.postMessage({ left, right }, [left.buffer, right.buffer]);
    this.at = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const left = input[0];
    // A merger with nothing on channel 1 hands us one channel; duplicating it
    // would put the operator on both sides, so silence is the honest filler.
    const right = input.length > 1 ? input[1] : null;
    const frames = left ? left.length : 0;
    for (let i = 0; i < frames; i += 1) {
      this.left[this.at] = left[i];
      this.right[this.at] = right ? right[i] : 0;
      this.at += 1;
      if (this.at >= this.size) this.flush();
    }
    return true;
  }
}
registerProcessor("husly-pilot-tap", HuslyPilotTap);
`;

interface TapNode {
  node: AudioNode;
  dispose: () => void;
}

/** The worklet path. Throws when the browser or the CSP will not have it. */
async function createWorkletTap(
  ctx: AudioContext,
  onFrames: (left: Float32Array, right: Float32Array) => void,
): Promise<TapNode> {
  const url = URL.createObjectURL(new Blob([TAP_WORKLET], { type: "application/javascript" }));
  try {
    await ctx.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
  const node = new AudioWorkletNode(ctx, "husly-pilot-tap", {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [2],
    processorOptions: { batchFrames: TAP_BATCH_FRAMES },
  });
  node.port.onmessage = (event: MessageEvent<{ left: Float32Array; right: Float32Array }>) => {
    onFrames(event.data.left, event.data.right);
  };
  return {
    node,
    dispose: () => {
      node.port.onmessage = null;
      node.disconnect();
    },
  };
}

/**
 * The fallback for browsers without AudioWorklet (or with a CSP that refuses a
 * Blob module). ScriptProcessorNode is deprecated and runs on the main thread,
 * so a busy page can cost it samples - acceptable for a co-pilot, which is why
 * it is second choice rather than first.
 */
function createScriptTap(
  ctx: AudioContext,
  onFrames: (left: Float32Array, right: Float32Array) => void,
): TapNode {
  const node = ctx.createScriptProcessor(TAP_BATCH_FRAMES, 2, 2);
  node.onaudioprocess = (event) => {
    const input = event.inputBuffer;
    const left = new Float32Array(input.getChannelData(0));
    const right =
      input.numberOfChannels > 1 ? new Float32Array(input.getChannelData(1)) : new Float32Array(left.length);
    onFrames(left, right);
  };
  return {
    node,
    dispose: () => {
      node.onaudioprocess = null;
      node.disconnect();
    },
  };
}

/**
 * Chrome will not pull a REMOTE MediaStream into WebAudio unless the stream is
 * also attached to a media element. Twilio attaches its own for playback, but
 * relying on that is relying on somebody else's implementation detail: without
 * this element the right channel can come through as pure silence, and nothing
 * anywhere says why. Muted, so it adds no second copy of the caller's voice.
 *
 * Exported because the call window's meter needs exactly the same trick, and it
 * runs whether or not the AI panel is open - so it cannot rely on the tap below
 * having already attached one. Two elements on one stream is harmless; a missing
 * one is a caller who never moves the needle.
 */
export function attachRemoteSink(stream: MediaStream): () => void {
  const el = document.createElement("audio");
  el.srcObject = stream;
  el.muted = true;
  el.volume = 0;
  el.setAttribute("aria-hidden", "true");
  el.style.display = "none";
  document.body.appendChild(el);
  void el.play().catch(() => {
    // Autoplay refusal is harmless here: the element exists, which is the part
    // WebAudio needs, and nothing was going to be audible anyway.
  });
  return () => {
    el.pause();
    el.srcObject = null;
    el.remove();
  };
}

export function isCaptureSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.AudioContext === "function" &&
    typeof Blob === "function"
  );
}

/**
 * Start tapping a live call. Resolves once audio is flowing; rejects only if
 * there was nothing to tap. Everything that can go wrong afterwards arrives
 * through `onError` and leaves the call untouched.
 */
export async function startLiveCapture(options: LiveCaptureOptions): Promise<LiveCaptureHandle> {
  const { local, remote, onWindow, onError } = options;
  const windowMs = clampWindowMs(options.windowMs);

  if (!isCaptureSupported()) {
    onError("unsupported");
    throw new Error("capture_unsupported");
  }
  if (!local || local.getAudioTracks().length === 0) {
    onError("no_local_stream");
    throw new Error("capture_no_local");
  }
  if (!remote || remote.getAudioTracks().length === 0) {
    // Worth capturing anyway: half a transcript still carries what the operator
    // asked, and the panel says which half is missing.
    onError("no_remote_stream");
  }

  let ctx: AudioContext;
  try {
    ctx = new AudioContext({ sampleRate: PILOT_SAMPLE_RATE });
    // The click that started the call is the gesture, but the context can still
    // come up suspended and a suspended context produces no frames at all.
    if (ctx.state === "suspended") await ctx.resume();
  } catch {
    onError("context_failed");
    throw new Error("capture_context_failed");
  }

  const detachSink = remote ? attachRemoteSink(remote) : () => {};
  const startedAt = Date.now();
  const buffer = new WindowBuffer(Math.round((windowMs / 1000) * ctx.sampleRate), ctx.sampleRate);
  let seq = Math.max(0, Math.trunc(options.startSeq ?? 0));
  let stopped = false;

  const emit = (all: boolean): void => {
    const cut = buffer.take(all);
    if (!cut) return;
    const durationMs = Math.round((cut.frames / ctx.sampleRate) * 1000);
    if (all && durationMs < MIN_TAIL_MS) return;
    const blob = new Blob([encodeWavPcm16(cut.channels, ctx.sampleRate)], { type: "audio/wav" });
    onWindow({ seq: seq++, blob, startedAt: startedAt + Math.round(cut.offsetMs), durationMs });
  };

  let paused = false;

  const onFrames = (left: Float32Array, right: Float32Array): void => {
    if (stopped) return;
    if (paused) {
      // Push first, THEN discard. `discard` advances the clock by exactly what
      // it throws away, so dropping the frames without pushing them would stop
      // the clock instead of running it, and every window after a four-minute
      // hold would carry a `startedAt` four minutes early.
      buffer.push(left, right);
      buffer.discard();
      return;
    }
    buffer.push(left, right);
    while (buffer.ready) emit(false);
  };

  let tap: TapNode;
  try {
    tap = await createWorkletTap(ctx, onFrames);
  } catch {
    try {
      tap = createScriptTap(ctx, onFrames);
    } catch {
      detachSink();
      void ctx.close().catch(() => {});
      onError("tap_failed");
      throw new Error("capture_tap_failed");
    }
  }

  const merger = ctx.createChannelMerger(2);
  const sources: MediaStreamAudioSourceNode[] = [];
  const localSource = ctx.createMediaStreamSource(local);
  localSource.connect(merger, 0, 0);
  sources.push(localSource);
  if (remote && remote.getAudioTracks().length > 0) {
    const remoteSource = ctx.createMediaStreamSource(remote);
    remoteSource.connect(merger, 0, 1);
    sources.push(remoteSource);
  }
  merger.connect(tap.node);

  // A node nothing pulls from is a node that never runs: the graph is only
  // driven from the destination, so the tap has to reach it. The gain is zero,
  // so this adds a path to the speakers without adding a sound.
  const silence = ctx.createGain();
  silence.gain.value = 0;
  tap.node.connect(silence);
  silence.connect(ctx.destination);

  return {
    setPaused: (next: boolean) => {
      if (paused === next) return;
      paused = next;
      // Drop the part-window collected right up to the hold rather than gluing
      // it to whatever is said when the customer comes back.
      if (next) buffer.discard();
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      // The last thing said is usually the thing worth reading, so the partial
      // window goes out before the graph comes down.
      try {
        stopped = false;
        emit(true);
      } catch {
        // A failed tail is not worth a broken teardown.
      } finally {
        stopped = true;
      }
      tap.dispose();
      silence.disconnect();
      merger.disconnect();
      for (const source of sources) source.disconnect();
      detachSink();
      void ctx.close().catch(() => {});
    },
  };
}
